export const STAGE_SEQUENCE = [
  'hydrate',
  'goal_update',
  'intent',
  'plan',
  'retrieve',
  'summarize',
  'sufficiency',
  'refine',
  'consolidate',
  'select',
  'request',
  'stream',
  'finalize',
  'persist'
];

const EMPTY_ARRAY = Object.freeze([]);
const EMPTY_OBJECT = Object.freeze({});

export const createStageResult = ({
  stage,
  status = 'ok',
  decisionCode = null,
  reasonCodes = EMPTY_ARRAY,
  payload = EMPTY_OBJECT
} = {}) => {
  const normalizedStage = String(stage || '').trim().toLowerCase();
  if (!normalizedStage || !STAGE_SEQUENCE.includes(normalizedStage)) {
    throw new Error(`Unknown stage result: ${String(stage || 'unknown')}`);
  }

  return {
    stage: normalizedStage,
    status: String(status || 'ok').trim().toLowerCase() || 'ok',
    decisionCode: decisionCode ? String(decisionCode).trim() : null,
    reasonCodes: Array.isArray(reasonCodes)
      ? reasonCodes.map((value) => String(value || '').trim()).filter(Boolean)
      : [],
    payload: payload && typeof payload === 'object' ? payload : {}
  };
};

export const createRetrievePayload = ({
  retrievalIteration = 1,
  selectedCount = 0,
  selectedChars = 0,
  candidateCount = 0,
  selectedChunkIds = EMPTY_ARRAY,
  fetchedChunkIds = EMPTY_ARRAY,
  retrievalReasons = EMPTY_ARRAY,
  selectedSourceBreakdown = EMPTY_OBJECT,
  selectedDocBreakdown = EMPTY_ARRAY,
  selectionAudit = null,
  contextKeywordCoverage = null,
  ...rest
} = {}) => {
  return {
    retrievalIteration: Number(retrievalIteration || 1),
    selectedCount: Number(selectedCount || 0),
    selectedChars: Number(selectedChars || 0),
    candidateCount: Number(candidateCount || 0),
    selectedChunkIds: Array.isArray(selectedChunkIds) ? selectedChunkIds : [],
    fetchedChunkIds: Array.isArray(fetchedChunkIds) ? fetchedChunkIds : [],
    retrievalReasons: Array.isArray(retrievalReasons) ? retrievalReasons : [],
    selectedSourceBreakdown: selectedSourceBreakdown && typeof selectedSourceBreakdown === 'object'
      ? selectedSourceBreakdown
      : {},
    selectedDocBreakdown: Array.isArray(selectedDocBreakdown) ? selectedDocBreakdown : [],
    selectionAudit: selectionAudit && typeof selectionAudit === 'object' ? selectionAudit : null,
    contextKeywordCoverage: contextKeywordCoverage && typeof contextKeywordCoverage === 'object'
      ? contextKeywordCoverage
      : null,
    ...rest
  };
};

export const createSufficiencyPayload = ({
  score = 0,
  selectedCount = 0,
  selectedChars = 0,
  exhaustiveRequested = false,
  topDoc = null,
  topDocShare = 0,
  keywordCoverage = null,
  thresholds = null
} = {}) => {
  return {
    sufficiencyScore: Number(score || 0),
    selectedCount: Number(selectedCount || 0),
    selectedChars: Number(selectedChars || 0),
    exhaustiveRequested: Boolean(exhaustiveRequested),
    topDoc: topDoc && typeof topDoc === 'object' ? topDoc : null,
    topDocShare: Number(topDocShare || 0),
    keywordCoverage: keywordCoverage && typeof keywordCoverage === 'object' ? keywordCoverage : null,
    thresholds: thresholds && typeof thresholds === 'object' ? thresholds : null
  };
};

export const createConsolidatePayload = ({
  triggered = false,
  beforeContextItems = 0,
  beforeContextChars = 0,
  afterContextItems = 0,
  afterContextChars = 0,
  gateMinItems = 0,
  gateMinChars = 0,
  maxInputItems = 0,
  maxInputChars = 0,
  consolidationModel = null,
  consolidationOutputChars = 0,
  keptOriginalItems = 0
} = {}) => {
  return {
    triggered: Boolean(triggered),
    beforeContextItems: Number(beforeContextItems || 0),
    beforeContextChars: Number(beforeContextChars || 0),
    afterContextItems: Number(afterContextItems || 0),
    afterContextChars: Number(afterContextChars || 0),
    gateMinItems: Number(gateMinItems || 0),
    gateMinChars: Number(gateMinChars || 0),
    maxInputItems: Number(maxInputItems || 0),
    maxInputChars: Number(maxInputChars || 0),
    consolidationModel: consolidationModel ? String(consolidationModel).trim() : null,
    consolidationOutputChars: Number(consolidationOutputChars || 0),
    keptOriginalItems: Number(keptOriginalItems || 0)
  };
};
